<template>
    <v-container class="content-container">
        <v-row class="mt-12" v-if="loading.section || loading.document" justify="center">
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-text>
                        <v-row justify="center">
                            <v-col cols="auto">
                                <p class="caption">Loading Section</p>
                            </v-col>
                        </v-row>
                        <v-row justify="center">
                            <v-col cols="auto">
                                <v-progress-circular indeterminate color="primary" />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-else-if="error.section" justify="center">
            <v-col cols="12" md="8">
                <v-alert type="error" dense outlined>
                    {{ error.section }}
                </v-alert>
            </v-col>
        </v-row>

        <template v-else>
            <v-row v-if="contentSection" class="mb-4">
                <v-col>
                    <span class="font-weight-bold">Excerpt From:</span>
                    <a :href="excerptLink" class="primary--text" :aria-label="`Excerpt from ${contentSection.document.display_title}`">
                        {{ contentSection.document.display_title }}
                    </a>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <default-content-section-controller
                        v-if="contentSection"
                        :contentSections="[contentSection]"
                        :highlightType="getHighlightType"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="auto">
                    <img
                        class="book-cover my-3 rounded-lg"
                        :src="getBookCover(documentInfo)"
                        @error="loadDefaultBookCover"
                        :alt="getBookTitle(documentInfo)"
                    />
                </v-col>
                <v-col>
                    <v-row dense align="center">
                        <v-col>
                            <h2>{{ getBookTitle(documentInfo) }}</h2>
                        </v-col>
                        <v-col>
                            <v-row dense align="center">
                                <v-col cols="auto">
                                    <p class="mb-0 primary--text caption">Categories:</p>
                                </v-col>
                                <v-col>
                                    <v-chip
                                        class="ma-1"
                                        v-for="(item, key) in documentInfo.categories"
                                        :key="key"
                                        small
                                        @click="goToCategory(item.slug)"
                                    >
                                        {{ item.label }}
                                    </v-chip>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col>
                            <h3 class="mb-2 mt-3 primary--text font-weight-regular fs-22">
                                About this Title
                            </h3>
                            <div class="my-4" v-html="documentInfo.description" />
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>

            <v-row class="my-6">
                <v-col>
                    <v-img
                        class="cp"
                        @click="premiumSubscribe"
                        :src="asset(require('../../images/banners/unlock_premium_complete.webp'))"
                    />
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script>
import DefaultContentSectionController from '../../components/content-section/DefaultContentSectionController.vue';

export default {
    components: {
        DefaultContentSectionController
    },
    props: {
        documentId: {
            required: true,
            type: Number
        },
        chapterId: {
            required: true,
            type: Number
        },
        sectionHash: {
            required: true,
            type: String
        }
    },
    data() {
        return {
            loading: {
                section: true,
                document: true
            },
            error: {
                section: null
            },
            documentInfo: [],
            contentSection: null
        };
    },
    computed: {
        excerptLink() {
            return this.contentSection?.breadcrumbs?.[0]?.url || '#';
        },
        canBookmark() {
            return this.contentSection?.contentRestrictions?.canBookmark || false;
        },
        getHighlightType() {
            return this.canBookmark ? 'add-note' : 'none';
        }
    },
    methods: {
        premiumSubscribe() {
            this.getPremiumCompleteBook();
        },
        getTitleInfo() {
            this.$http.get(Routing.generate('get_book_info', {
                documentId: this.documentId
            })).then(response => {
                if (response.data) {
                    this.documentInfo = response.data;
                }
                this.$set(this.loading, 'document', false);
            });
        },
        getSectionContent() {
            this.$http.get(Routing.generate('get_snippet', {xmlId: this.sectionHash}))
                .then(response => {
                    const res = response.data;
                    if (res?.error) {
                        this.error.section = res.error;
                        this.contentSection = null;
                        return;
                    }

                    if (!res?.content) {
                        this.error.section = 'No content received from server.';
                        this.contentSection = null;
                        return;
                    }

                    const content = res.content;

                    this.contentSection = {
                        sectionId: content.sectionId,
                        documentId: this.documentId,
                        title: content.title,
                        content: content.content,
                        document: {
                            display_title: content.display_title
                        },
                        insights: content.insights || [],
                        bookmarks: content.bookmarks || [],
                        breadcrumbs: content.breadcrumbs || [],
                        contentRestrictions: content.contentRestrictions || {}
                    };

                    const titleParts = [content.display_title, content.title].filter(Boolean);
                    document.title = titleParts.join(' - ');
                    document.querySelector('meta[name="description"]').setAttribute("content", '');
                    document.querySelector('meta[name="keywords"]').setAttribute("content", '');
                })
                .catch(error => {
                    console.error('Failed to fetch content:', error);
                    this.error.section = 'An unexpected error occurred while loading the section.';
                    this.contentSection = null;
                })
                .finally(() => {
                    this.$set(this.loading, 'section', false);
                });
        },
        goToCategory(categorySlug) {
            this.goToRoute('codes_category', { category: categorySlug });
        },
        goToChapter() {
            this.goToRoute('content_document', {
                documentId: this.documentId,
                chapterId: this.chapterId
            });
        }
    },
    mounted() {
        this.getTitleInfo();
        this.getSectionContent();
    }
};
</script>

<style scoped>
.book-cover {
    width: 100px;
    height: auto;
}
.fs-22 {
    font-size: 22px;
}
.cp {
    cursor: pointer;
}
</style>
